import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  User,
  Mail,
  Calendar,
  FileText,
  BookOpen,
  Activity,
  CheckCircle2,
  Clock,
  ChevronRight,
  Edit,
  Save,
  Camera,
  MessageSquare,
  Sparkles,
  Shield,
  UserCog,
  UserX,
  Trash2,
  UserCheck,
  ClipboardCheck,
  TrendingUp,
  BarChart3,
  Target
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { ProgressLineChart, FruitsRadarChart } from "@/components/CheckInCharts";

export default function StudentProfile({ user }) {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [checkinSummary, setCheckinSummary] = useState('');
  const [checkinSummaryGeneratedAt, setCheckinSummaryGeneratedAt] = useState(null);
  const [checkinSummaryLoading, setCheckinSummaryLoading] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Check if viewer can see private information (teacher or viewing own profile)
  const canViewPrivateInfo = user.role === 'teacher' || user.user_id === studentId;
  const canEditGrowthFocus = user.role === 'teacher' || user.is_super_admin;
  const [upgrading, setUpgrading] = useState(false);
  const [settingInactive, setSettingInactive] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const [growthFocus, setGrowthFocus] = useState('');
  const [growthFocusContext, setGrowthFocusContext] = useState('');
  const [savingGrowthFocus, setSavingGrowthFocus] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    bio: '',
    favorite_verse: '',
    picture: ''
  });

  useEffect(() => {
    fetchStudentData();
  }, [studentId]);

  const fetchStudentData = async () => {
    try {
      // Fetch student info
      const studentRes = await axios.get(`${API}/users/${studentId}`);
      setStudent(studentRes.data);
      setGrowthFocus(studentRes.data.growth_focus || '');
      setGrowthFocusContext(studentRes.data.growth_focus_context || '');
      setCheckinSummary(studentRes.data.ai_checkin_summary || '');
      setCheckinSummaryGeneratedAt(studentRes.data.ai_checkin_summary_generated_at || null);
      setEditForm({
        name: studentRes.data.name || '',
        bio: studentRes.data.bio || '',
        favorite_verse: studentRes.data.favorite_verse || '',
        picture: studentRes.data.picture || ''
      });

      // Fetch student details (submissions, journals, activities)
      const [submissionsRes, journalsRes, assignmentsRes, checkInsRes] = await Promise.all([
        axios.get(`${API}/submissions`),
        axios.get(`${API}/journals`),
        axios.get(`${API}/assignments`),
        user.role === 'teacher' ? axios.get(`${API}/checkins/user/${studentId}?limit=10`) : Promise.resolve({ data: [] })
      ]);
      
      // Activities are only available for teachers
      let activitiesRes = { data: [] };
      if (user.role === 'teacher') {
        try {
          activitiesRes = await axios.get(`${API}/activities?limit=50`);
        } catch (e) {
          console.warn("Could not fetch activities:", e);
        }
      }

      // Set check-ins for teachers
      if (user.role === 'teacher') {
        setCheckIns(checkInsRes.data);
      }

      // Filter for this student
      const submissions = submissionsRes.data.filter(s => s.student_id === studentId);
      const journals = journalsRes.data.filter(j => j.student_id === studentId);
      const activities = activitiesRes.data.filter(a => a.user_id === studentId);
      
      // Build assignment lookup
      const assignmentLookup = {};
      assignmentsRes.data.forEach(a => {
        assignmentLookup[a.assignment_id] = a;
      });

      // Get pending actions
      const pendingSubmissions = submissions.filter(s => s.status === 'submitted');
      const pendingJournals = journals.filter(j => j.status === 'submitted');
      
      // Get unsubmitted assignments (assignments without a submission from this student)
      const submittedAssignmentIds = submissions.map(s => s.assignment_id);
      const unsubmittedAssignments = assignmentsRes.data.filter(a => 
        !submittedAssignmentIds.includes(a.assignment_id) &&
        (!a.assigned_to || a.assigned_to === studentId)
      );

      setStudentDetails({
        submissions,
        journals,
        activities,
        assignmentLookup,
        pendingActions: {
          pendingSubmissions,
          pendingJournals,
          unsubmittedAssignments
        },
        stats: {
          totalSubmissions: submissions.length,
          gradedSubmissions: submissions.filter(s => s.status === 'graded').length,
          totalJournals: journals.length,
          reviewedJournals: journals.filter(j => j.status === 'reviewed').length
        }
      });
    } catch (error) {
      console.error("Error fetching student data:", error);
      toast.error("Failed to load student profile");
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = `/api/files/${response.data.file_id}`;
      setEditForm(prev => ({ ...prev, picture: fileUrl }));
      toast.success("Image uploaded");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const saveProfile = async () => {
    if (!student) return;
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/${studentId}`, editForm);
      setStudent(response.data);
      setEditMode(false);
      toast.success("Student profile updated");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const saveGrowthFocus = async () => {
    setSavingGrowthFocus(true);
    try {
      const response = await axios.put(`${API}/users/${studentId}/growth-focus`, {
        growth_focus: growthFocus || null,
        growth_focus_context: growthFocusContext || null
      });
      setStudent(response.data);
      toast.success("Growth Focus saved");
    } catch (error) {
      console.error("Error saving growth focus:", error);
      toast.error(error.response?.data?.detail || "Failed to save Growth Focus");
    } finally {
      setSavingGrowthFocus(false);
    }
  };

  const upgradeToTeacher = async () => {
    setUpgrading(true);
    try {
      await axios.put(`${API}/users/${studentId}/upgrade-to-teacher`);
      toast.success(`${student.name} has been upgraded to Teacher/Admin`);
      setShowUpgradeDialog(false);
      // Redirect back to students list since this user is no longer a student
      navigate('/students');
    } catch (error) {
      console.error("Error upgrading user:", error);
      toast.error("Failed to upgrade user");
    } finally {
      setUpgrading(false);
    }
  };

  const toggleInactiveStatus = async () => {
    setSettingInactive(true);
    try {
      const isCurrentlyInactive = student.is_active === false;
      const endpoint = isCurrentlyInactive 
        ? `${API}/users/${studentId}/set-active`
        : `${API}/users/${studentId}/set-inactive`;
      
      await axios.put(endpoint);
      
      const action = isCurrentlyInactive ? 'reactivated' : 'set as inactive';
      toast.success(`${student.name} has been ${action}`);
      setShowInactiveDialog(false);
      
      // Refresh student data
      const studentRes = await axios.get(`${API}/users/${studentId}`);
      setStudent(studentRes.data);
    } catch (error) {
      console.error("Error toggling inactive status:", error);
      toast.error(error.response?.data?.detail || "Failed to update user status");
    } finally {
      setSettingInactive(false);
    }
  };

  const deleteStudent = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${studentId}`);
      toast.success(`${student.name} has been deleted`);
      setShowDeleteDialog(false);
      navigate('/students');
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const generateJournalSummary = async () => {
    if (!studentDetails?.journals || studentDetails.journals.length === 0) {
      toast.error("No journals to summarize");
      return;
    }

    setSummaryLoading(true);
    try {
      // Compile recent journals text
      const recentJournals = studentDetails.journals.slice(0, 10);
      const journalsText = recentJournals.map(j => 
        `Title: ${j.title}\nContent: ${j.content.substring(0, 500)}`
      ).join('\n\n---\n\n');

      const response = await axios.post(`${API}/ai/bible-verse`, {
        prompt: `As a Christian discipleship teacher, please provide a two-paragraph summary of this student's recent journal entries. Focus on their spiritual growth, recurring themes, prayer requests, and areas where they might need encouragement or guidance. Be compassionate and insightful.\n\nJournal Entries:\n${journalsText}`
      });
      
      setAiSummary(response.data.suggestion);
    } catch (error) {
      console.error("Error generating summary:", error);
      toast.error("Failed to generate summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  const generateCheckinSummary = async () => {
    if (checkIns.length === 0) {
      toast.error("No check-ins to summarize yet");
      return;
    }
    setCheckinSummaryLoading(true);
    try {
      const response = await axios.post(`${API}/users/${studentId}/checkin-summary`);
      setCheckinSummary(response.data.summary);
      setCheckinSummaryGeneratedAt(response.data.generated_at);
    } catch (error) {
      console.error("Error generating check-in summary:", error);
      toast.error(error.response?.data?.detail || "Failed to generate summary");
    } finally {
      setCheckinSummaryLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout user={user}>
        <div className="text-center py-16">
          <p className="text-slate-600">Student not found</p>
          <Button variant="link" onClick={() => navigate('/students')}>
            Back to Students
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="student-profile-page" className="space-y-8">
        {/* Back Button */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/students')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-slate-600">Back to Students</span>
        </div>

        {/* Profile Header Card */}
        <Card className="border-slate-100 overflow-hidden">
          {/* Cover Image */}
          <div className="h-32 bg-gradient-to-r from-slate-800 to-slate-700"></div>
          
          <CardContent className="relative pt-0">
            {/* Avatar */}
            <div className="absolute -top-16 left-6">
              <div className="relative">
                <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                  <AvatarImage src={editMode ? editForm.picture : student.picture} />
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-3xl">
                    {getInitials(editMode ? editForm.name : student.name)}
                  </AvatarFallback>
                </Avatar>
                {editMode && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      size="icon"
                      variant="secondary"
                      className="absolute bottom-2 right-2 rounded-full w-8 h-8"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="pt-20 pb-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  {editMode ? (
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="font-serif text-2xl font-bold mb-2"
                      placeholder="Student name"
                    />
                  ) : (
                    <div className="flex items-center gap-3">
                      <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
                        {student.name}
                      </h1>
                      {student.is_active === false && (
                        <Badge variant="outline" className="border-orange-300 text-orange-700 bg-orange-50">
                          Inactive
                        </Badge>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    <span>{student.email}</span>
                  </div>
                  {student.created_at && (
                    <div className="flex items-center gap-3 mt-1 text-slate-500 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {format(new Date(student.created_at), 'MMMM d, yyyy')}</span>
                    </div>
                  )}
                  {student.last_login && (
                    <div className="flex items-center gap-3 mt-1 text-slate-500 text-sm">
                      <Clock className="w-4 h-4" />
                      <span>Last login: {format(new Date(student.last_login), 'MMMM d, yyyy \'at\' h:mm a')}</span>
                    </div>
                  )}
                  {student.last_activity && (
                    <div className="flex items-center gap-3 mt-1 text-slate-500 text-sm">
                      <Activity className="w-4 h-4" />
                      <span>Last activity: {format(new Date(student.last_activity.created_at), 'MMMM d, yyyy \'at\' h:mm a')}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {user.role === 'teacher' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (editMode) {
                            setEditForm({
                              name: student.name || '',
                              bio: student.bio || '',
                              favorite_verse: student.favorite_verse || '',
                              picture: student.picture || ''
                            });
                          }
                          setEditMode(!editMode);
                        }}
                        className="rounded-full"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        {editMode ? 'Cancel' : 'Edit Profile'}
                      </Button>
                      {editMode && (
                        <Button
                          onClick={saveProfile}
                          disabled={saving}
                          className="bg-slate-800 hover:bg-slate-900 rounded-full"
                        >
                          {saving ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setShowUpgradeDialog(true)}
                        className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Make Admin
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowInactiveDialog(true)}
                        className={`rounded-full ${
                          student.is_active === false 
                            ? 'border-green-300 text-green-700 hover:bg-green-50'
                            : 'border-orange-300 text-orange-700 hover:bg-orange-50'
                        }`}
                      >
                        {student.is_active === false ? (
                          <>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Reactivate
                          </>
                        ) : (
                          <>
                            <UserX className="w-4 h-4 mr-2" />
                            Set Inactive
                          </>
                        )}
                      </Button>
                      {user.is_super_admin && (
                        <Button
                          variant="outline"
                          onClick={() => setShowDeleteDialog(true)}
                          className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    onClick={() => navigate(`/messages/${studentId}`)}
                    className="bg-amber-600 hover:bg-amber-700 rounded-full"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                </div>
              </div>

              {/* Bio & Favorite Verse */}
              {editMode ? (
                <div className="mt-6 space-y-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-amber-600" />
                      Favorite Bible Verse
                    </Label>
                    <Input
                      value={editForm.favorite_verse}
                      onChange={(e) => setEditForm({ ...editForm, favorite_verse: e.target.value })}
                      placeholder="e.g., Philippians 4:13"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={editForm.bio}
                      onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                      placeholder="About this student..."
                      className="mt-1 min-h-[100px]"
                    />
                  </div>
                </div>
              ) : (
                <>
                  {student.favorite_verse && (
                    <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <BookOpen className="w-4 h-4 text-amber-700" />
                        <span className="text-xs font-medium text-amber-700">Favorite Verse</span>
                      </div>
                      <p className="text-amber-900 font-serif">{student.favorite_verse}</p>
                    </div>
                  )}
                  {student.bio && (
                    <p className="mt-4 text-slate-600">{student.bio}</p>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Growth Focus - "one thing to work on" */}
        {canViewPrivateInfo && (
          <Card data-testid="growth-focus-section" className="border-teal-200 bg-gradient-to-r from-teal-50/60 to-emerald-50/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center text-teal-900">
                <Target className="w-5 h-5 mr-2 text-teal-700" />
                Growth Focus
              </CardTitle>
              <CardDescription>The one thing {user.user_id === studentId ? 'you are' : `${student.name} is`} focused on right now</CardDescription>
            </CardHeader>
            <CardContent>
              {canEditGrowthFocus ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-teal-900">Growth Focus</Label>
                    <Textarea
                      data-testid="growth-focus-input"
                      value={growthFocus}
                      onChange={(e) => setGrowthFocus(e.target.value)}
                      placeholder="e.g., Practicing daily prayer and journaling consistency"
                      className="bg-white min-h-[70px] mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-teal-900">Growth Context</Label>
                    <p className="text-xs text-teal-700/70 mb-1.5">Optional — more detail on why this focus was chosen or specific examples</p>
                    <Textarea
                      data-testid="growth-focus-context-input"
                      value={growthFocusContext}
                      onChange={(e) => setGrowthFocusContext(e.target.value)}
                      placeholder="e.g., They mentioned struggling to find quiet time in the mornings — encourage a short 5-minute prayer before work."
                      className="bg-white min-h-[90px]"
                    />
                  </div>
                  <Button
                    data-testid="save-growth-focus-btn"
                    onClick={saveGrowthFocus}
                    disabled={savingGrowthFocus}
                    className="bg-teal-700 hover:bg-teal-800 rounded-full"
                  >
                    {savingGrowthFocus ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save
                  </Button>
                </div>
              ) : (
                student.growth_focus ? (
                  <div className="space-y-2">
                    <p className="text-teal-900">{student.growth_focus}</p>
                    {student.growth_focus_context && (
                      <p className="text-sm text-teal-700/80 border-t border-teal-200 pt-2">{student.growth_focus_context}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No Growth Focus set yet.</p>
                )
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        {studentDetails && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-100">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-slate-900">{studentDetails.stats.totalSubmissions}</p>
                <p className="text-sm text-slate-600 mt-1">Assignments</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-green-700">{studentDetails.stats.gradedSubmissions}</p>
                <p className="text-sm text-slate-600 mt-1">Graded</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-amber-700">{studentDetails.stats.totalJournals}</p>
                <p className="text-sm text-slate-600 mt-1">Journals</p>
              </CardContent>
            </Card>
            <Card className="border-teal-200 bg-teal-50/50">
              <CardContent className="pt-6 text-center">
                <p className="text-3xl font-bold text-teal-700">{studentDetails.stats.reviewedJournals}</p>
                <p className="text-sm text-slate-600 mt-1">Reviewed</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recent Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pending Actions - Only visible to teachers or the student themselves */}
            {canViewPrivateInfo && studentDetails?.pendingActions && (
              (studentDetails.pendingActions.pendingSubmissions.length > 0 ||
               studentDetails.pendingActions.pendingJournals.length > 0 ||
               studentDetails.pendingActions.unsubmittedAssignments.length > 0) && (
                <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center text-amber-900">
                      <Clock className="w-5 h-5 mr-2 text-amber-600" />
                      Pending Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {studentDetails.pendingActions.pendingSubmissions.map((sub) => (
                      <div 
                        key={sub.submission_id}
                        className="flex items-center justify-between p-3 bg-white/70 rounded-lg cursor-pointer hover:bg-white"
                        onClick={() => navigate(`/assignments/${sub.assignment_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-slate-700">
                            Assignment needs grading: {studentDetails.assignmentLookup[sub.assignment_id]?.title || 'Assignment'}
                          </span>
                        </div>
                        <Badge className="bg-amber-500">Review</Badge>
                      </div>
                    ))}
                    {studentDetails.pendingActions.pendingJournals.map((journal) => (
                      <div 
                        key={journal.journal_id}
                        className="flex items-center justify-between p-3 bg-white/70 rounded-lg cursor-pointer hover:bg-white"
                        onClick={() => navigate(`/journals/${journal.journal_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <BookOpen className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-slate-700">
                            Journal needs review: {journal.title}
                          </span>
                        </div>
                        <Badge className="bg-amber-500">Review</Badge>
                      </div>
                    ))}
                    {studentDetails.pendingActions.unsubmittedAssignments.slice(0, 3).map((assignment) => (
                      <div 
                        key={assignment.assignment_id}
                        className="flex items-center justify-between p-3 bg-white/70 rounded-lg cursor-pointer hover:bg-white"
                        onClick={() => navigate(`/assignments/${assignment.assignment_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700">
                            Not submitted: {assignment.title}
                          </span>
                        </div>
                        <Badge variant="outline">Missing</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )
            )}

            {/* Recent Submissions - Only visible to teachers or the student themselves */}
            {canViewPrivateInfo && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-slate-600" />
                  Recent Submissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!studentDetails?.submissions || studentDetails.submissions.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No submissions yet</p>
                ) : (
                  <div className="space-y-2">
                    {studentDetails.submissions.slice(0, 5).map((sub) => (
                      <div 
                        key={sub.submission_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                        onClick={() => navigate(`/assignments/${sub.assignment_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          {sub.status === 'graded' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-600" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {studentDetails.assignmentLookup[sub.assignment_id]?.title || 'Assignment'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(sub.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={sub.status === 'graded' ? 'default' : 'secondary'}>
                            {sub.status}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Recent Journals - Only visible to teachers or the student themselves */}
            {canViewPrivateInfo && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-amber-600" />
                  Recent Journals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!studentDetails?.journals || studentDetails.journals.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No journals yet</p>
                ) : (
                  <div className="space-y-2">
                    {studentDetails.journals.slice(0, 5).map((journal) => (
                      <div 
                        key={journal.journal_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                        onClick={() => navigate(`/journals/${journal.journal_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          {journal.status === 'reviewed' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-600" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-900">{journal.title}</p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(journal.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={journal.status === 'reviewed' ? 'default' : 'secondary'}>
                            {journal.status}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Weekly Check-Ins - Teachers Only */}
            {user.role === 'teacher' && (
            <Card className="border-slate-100">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center">
                  <ClipboardCheck className="w-5 h-5 mr-2 text-green-600" />
                  Weekly Check-Ins
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/checkins?student=${studentId}`)}
                  className="text-slate-600"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {checkIns.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No check-ins submitted yet</p>
                ) : (
                  <div className="space-y-4">
                    {/* Progress Chart */}
                    {checkIns.length >= 2 && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp className="w-4 h-4 text-green-600" />
                          <p className="text-sm font-medium text-slate-700">Progress Over Time</p>
                        </div>
                        <ProgressLineChart checkIns={checkIns} />
                      </div>
                    )}

                    {/* Latest Check-in Fruits Radar */}
                    {checkIns.length > 0 && checkIns[0].fruits_of_spirit && (
                      <div className="bg-amber-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-amber-600" />
                          <p className="text-sm font-medium text-amber-700">Latest Fruits of Spirit</p>
                        </div>
                        <FruitsRadarChart data={checkIns[0].fruits_of_spirit} />
                      </div>
                    )}

                    {/* AI Check-In Summary */}
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100" data-testid="checkin-summary-section">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-indigo-600" />
                          <p className="text-sm font-medium text-indigo-900">What's Going On — AI Summary</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={generateCheckinSummary}
                          disabled={checkinSummaryLoading}
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 rounded-full"
                          data-testid="generate-checkin-summary-btn"
                        >
                          {checkinSummaryLoading ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-indigo-400/40 border-t-indigo-700 rounded-full animate-spin mr-2"></div>
                              Generating...
                            </>
                          ) : (
                            checkinSummary ? 'Regenerate' : 'Generate Summary'
                          )}
                        </Button>
                      </div>
                      {checkinSummary ? (
                        <div>
                          <p className="text-sm text-indigo-900/90 whitespace-pre-wrap" data-testid="checkin-summary-text">{checkinSummary}</p>
                          {checkinSummaryGeneratedAt && (
                            <p className="text-xs text-indigo-500 mt-2">
                              Based on their last {Math.min(checkIns.length, 3)} check-in{Math.min(checkIns.length, 3) === 1 ? '' : 's'} • Generated {format(new Date(checkinSummaryGeneratedAt), 'MMM d, yyyy h:mm a')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-indigo-600/70">
                          Generate a short summary of {student.name}'s last {Math.min(checkIns.length, 3)} check-in{Math.min(checkIns.length, 3) === 1 ? '' : 's'} — what's going well and any concerns to watch for.
                        </p>
                      )}
                    </div>

                    {/* Recent Check-ins List */}
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">RECENT CHECK-INS</p>
                      {checkIns.slice(0, 5).map((checkIn) => (
                        <div 
                          key={checkIn.checkin_id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 mb-2"
                          onClick={() => navigate(`/checkins?student=${studentId}`)}
                        >
                          <div className="flex items-center gap-3">
                            <ClipboardCheck className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                Week of {format(new Date(checkIn.week_start), 'MMM d, yyyy')}
                              </p>
                              <p className="text-xs text-slate-500">
                                Loved God: {checkIn.loved_well?.loved_god}/10 • Loved People: {checkIn.loved_well?.loved_people}/10
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!checkIn.was_honest && (
                              <Badge variant="outline" className="text-amber-600 border-amber-300">
                                Not Honest
                              </Badge>
                            )}
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </div>

          {/* Right Column - Activity Feed & AI Summary */}
          <div className="space-y-6">
            {/* AI Journal Summary (Teachers Only) */}
            {user.role === 'teacher' && studentDetails?.journals && studentDetails.journals.length > 0 && (
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-indigo-50/50">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center">
                    <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                    AI Journal Summary
                  </CardTitle>
                  <CardDescription>
                    Get an AI-generated overview of this student&apos;s journal entries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {aiSummary ? (
                    <div className="text-sm text-slate-700 whitespace-pre-wrap mb-4">
                      {aiSummary}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 mb-4">
                      Generate a summary of {student.name}&apos;s recent journals to better understand their spiritual journey.
                    </p>
                  )}
                  <Button 
                    onClick={generateJournalSummary}
                    disabled={summaryLoading}
                    className="w-full bg-purple-600 hover:bg-purple-700 rounded-full"
                  >
                    {summaryLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        {aiSummary ? 'Refresh Summary' : 'Generate Summary'}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Recent Activity - Only visible to teachers or the student themselves */}
            {canViewPrivateInfo && (
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-sm flex items-center">
                  <Activity className="w-4 h-4 mr-2 text-teal-600" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!studentDetails?.activities || studentDetails.activities.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">No recent activity</p>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {studentDetails.activities.slice(0, 20).map((activity) => (
                        <div 
                          key={activity.activity_id}
                          className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0"
                        >
                          <div className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700">{activity.description}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade to Teacher/Admin Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Upgrade to Teacher/Admin
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to upgrade <strong>{student?.name}</strong> to a Teacher/Admin role?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <strong>This action will:</strong>
              </p>
              <ul className="text-sm text-purple-700 mt-2 space-y-1 list-disc list-inside">
                <li>Grant full admin privileges to this user</li>
                <li>Allow them to create assignments and review submissions</li>
                <li>Allow them to manage other students</li>
                <li>Move them from the Students list to the Teachers group</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUpgradeDialog(false)}
              disabled={upgrading}
            >
              Cancel
            </Button>
            <Button
              onClick={upgradeToTeacher}
              disabled={upgrading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {upgrading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Upgrading...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Confirm Upgrade
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Inactive Dialog */}
      <Dialog open={showInactiveDialog} onOpenChange={setShowInactiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {student?.is_active === false ? (
                <>
                  <UserCheck className="w-5 h-5 text-green-600" />
                  Reactivate Student
                </>
              ) : (
                <>
                  <UserX className="w-5 h-5 text-orange-600" />
                  Set Student Inactive
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {student?.is_active === false 
                ? `Are you sure you want to reactivate ${student?.name}?`
                : `Are you sure you want to set ${student?.name} as inactive?`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className={`p-4 rounded-lg border ${
              student?.is_active === false 
                ? 'bg-green-50 border-green-200'
                : 'bg-orange-50 border-orange-200'
            }`}>
              <p className={`text-sm ${student?.is_active === false ? 'text-green-800' : 'text-orange-800'}`}>
                <strong>This action will:</strong>
              </p>
              <ul className={`text-sm mt-2 space-y-1 list-disc list-inside ${
                student?.is_active === false ? 'text-green-700' : 'text-orange-700'
              }`}>
                {student?.is_active === false ? (
                  <>
                    <li>Make the student visible in the students list</li>
                    <li>Allow them to log in and participate again</li>
                    <li>Restore their access to assignments and journals</li>
                  </>
                ) : (
                  <>
                    <li>Hide the student from the active students list</li>
                    <li>Prevent them from appearing in assignments</li>
                    <li>Their data will be preserved for records</li>
                  </>
                )}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInactiveDialog(false)}
              disabled={settingInactive}
            >
              Cancel
            </Button>
            <Button
              onClick={toggleInactiveStatus}
              disabled={settingInactive}
              className={student?.is_active === false 
                ? "bg-green-600 hover:bg-green-700"
                : "bg-orange-600 hover:bg-orange-700"
              }
            >
              {settingInactive ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Processing...
                </>
              ) : student?.is_active === false ? (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Reactivate
                </>
              ) : (
                <>
                  <UserX className="w-4 h-4 mr-2" />
                  Set Inactive
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Student Dialog (Super Admin Only) */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Student
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>{student?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                <strong>Warning: This action is irreversible!</strong>
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>The student&apos;s account will be permanently removed</li>
                <li>They will no longer be able to log in</li>
                <li>Their profile data will be deleted</li>
                <li>Consider setting them as inactive instead if you want to preserve their data</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteStudent}
              disabled={deleting}
              variant="destructive"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
